import Button from '@atoms/Button/Button';
import Modal from '@atoms/Modal/Modal';
import { ILink } from '@models/ILink';
import { IMessage } from '@models/IMessage';
import { IPricingData } from '@models/IPricingData';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IProductTrackingData } from '@models/IProductTrackingData';
import { ISignupForm } from '@models/ISignupForm';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import InfoModal from '@molecules/InfoModal/InfoModal';
import SignupForm from '@organisms/SignupForm/SignupForm';
import signupFormGetProps from '@services/signupFormGetProps';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import classNames from 'classnames';
import { useState } from 'react';

interface Props {
  readonly backOrderText: IMessage;
  readonly buyNowLabel?: string;
  readonly buyNowUrl?: string;
  readonly dataTestId?: string;
  readonly directionClassName?: string;
  readonly disableFindDealerCta?: boolean;
  readonly dealerFindLink: ILink;
  readonly gapClassName?: string;
  readonly notifyMeContactFormEndpoint: string;
  readonly preOrderText: IMessage;
  readonly pricingData?: IPricingData;
  readonly sharedButtonsClassName?: string;
  readonly skuCode: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly trackingData?: IProductTrackingData;
  readonly onAddProductToCart: () => Promise<void>;
}

const ProductDetailsCta = ({
  backOrderText,
  buyNowLabel,
  buyNowUrl,
  dataTestId = 'Product-Details-CTA',
  directionClassName = 'lg:flex-row',
  disableFindDealerCta,
  dealerFindLink,
  gapClassName = 'gap-5',
  notifyMeContactFormEndpoint,
  preOrderText,
  pricingData,
  sharedButtonsClassName,
  skuCode,
  statusLabels,
  trackingData,
  onAddProductToCart,
}: Props) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState<IMessage>({
    title: '',
    text: '',
  });
  const [isSignupFormOpen, setIsSignupFormOpen] = useState(false);
  const [signupFormData, setSignupFormData] = useState<ISignupForm>();
  const stockStatus = pricingData?.status;

  const handleInfoModal = (content: IMessage) => {
    setInfoModalContent(content);
    setIsInfoModalOpen(true);
  };

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const handleFindDealerClick = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'intentToBuy',
      content_type: dealerFindLink?.name || 'Find dealer',
      items: [mapToNewTrackingData(trackingData, { hideIndex: true })],
    });
  };

  const notifyMeHandler = async () => {
    try {
      const signupFormResponse = await signupFormGetProps(
        notifyMeContactFormEndpoint
      );

      setSignupFormData(signupFormResponse);
      setIsSignupFormOpen(true);
    } catch {
      setErrorBannerNotification(
        ErrorBannerNotificationEnum.ProductDetailsNotifyMe
      );
    }
  };

  const handleModalAddToCartClick = async () => {
    await onAddProductToCart();
    setIsInfoModalOpen(false);
  };

  return (
    <div
      className={classNames(
        'flex flex-col flex-wrap items-start justify-start',
        gapClassName,
        directionClassName
      )}
      data-testid={dataTestId}
    >
      {stockStatus === ProductStockStatusEnum.InStock && (
        <>
          {!!pricingData?.price && (
            <Button
              className={sharedButtonsClassName}
              onClick={onAddProductToCart}
              dataTestId={`${dataTestId}-Add-To-Cart`}
            >
              {statusLabels.addToCartCTA}
            </Button>
          )}
          {!disableFindDealerCta &&
            dealerFindLink?.name &&
            dealerFindLink?.url && (
              <Button
                className={sharedButtonsClassName}
                data-testid="find-dealer-button"
                secondary
                isLightMode
                link={dealerFindLink}
                linkCallback={handleFindDealerClick}
                dataTestId={`${dataTestId}-Find-Dealer`}
              />
            )}
        </>
      )}

      {buyNowLabel && buyNowUrl && (
        <Button
          className={sharedButtonsClassName}
          link={{ url: buyNowUrl, name: buyNowLabel }}
          dataTestId={`${dataTestId}-Buy-Now`}
        />
      )}

      {(stockStatus === ProductStockStatusEnum.PreOrder ||
        stockStatus === ProductStockStatusEnum.BackOrder) && (
        <>
          {!!pricingData?.price && (
            <Button
              className={sharedButtonsClassName}
              onClick={() =>
                handleInfoModal(
                  pricingData.status === ProductStockStatusEnum.PreOrder
                    ? preOrderText
                    : backOrderText
                )
              }
              dataTestId={`${dataTestId}-PreOrder/BackOrder`}
            >
              {statusLabels.addToCartCTA}
            </Button>
          )}

          {!disableFindDealerCta &&
            dealerFindLink?.name &&
            dealerFindLink?.url && (
              <Button
                className={sharedButtonsClassName}
                data-testid="find-dealer-button"
                secondary
                isLightMode
                link={dealerFindLink}
                linkCallback={handleFindDealerClick}
                dataTestId={`${dataTestId}-Find-Dealer`}
              />
            )}

          {!!pricingData?.price && (
            <Button
              className={sharedButtonsClassName}
              onClick={notifyMeHandler}
              dataTestId={`${dataTestId}-Notifications-Signup`}
            >
              {statusLabels.signUpForNotificationsCTA}
            </Button>
          )}
        </>
      )}

      {(stockStatus === ProductStockStatusEnum.OutOfStock ||
        stockStatus === ProductStockStatusEnum.ComingSoon) && (
        <>
          {!!pricingData?.price && (
            <Button
              className={sharedButtonsClassName}
              onClick={notifyMeHandler}
              dataTestId={`${dataTestId}-Notifications-Signup`}
            >
              {statusLabels.signUpForNotificationsCTA}
            </Button>
          )}
          {!disableFindDealerCta &&
            dealerFindLink?.name &&
            dealerFindLink?.url && (
              <Button
                className={sharedButtonsClassName}
                secondary
                isLightMode
                link={dealerFindLink}
                linkCallback={handleFindDealerClick}
                dataTestId={`${dataTestId}-Find-Dealer`}
              />
            )}
        </>
      )}

      {(!stockStatus ||
        stockStatus === ProductStockStatusEnum.SoldOut ||
        stockStatus === ProductStockStatusEnum.DoNotShow ||
        stockStatus === ProductStockStatusEnum.BtoBOnly) &&
        !disableFindDealerCta &&
        dealerFindLink?.name &&
        dealerFindLink?.url && (
          <Button
            className={sharedButtonsClassName}
            link={dealerFindLink}
            linkCallback={handleFindDealerClick}
            secondary={!!(buyNowLabel && buyNowUrl)}
            isLightMode
            dataTestId={`${dataTestId}-Find-Dealer`}
          />
        )}

      <InfoModal
        type={InfoTypeEnum.Information}
        isModalOpen={isInfoModalOpen}
        toggleModal={setIsInfoModalOpen}
        title={infoModalContent.title}
        description={infoModalContent.text}
        buttonText={statusLabels.addToCartCTA}
        handleCTAClick={handleModalAddToCartClick}
        dataTestId={`${dataTestId}-Info-Modal`}
      />

      <Modal
        isOpen={isSignupFormOpen}
        setIsOpen={setIsSignupFormOpen}
        showCloseButton={false}
        transparent
        contentClasses="py-4"
      >
        {signupFormData && (
          <SignupForm
            {...signupFormData}
            productCode={skuCode}
            closeCallback={() => setIsSignupFormOpen(!isSignupFormOpen)}
          />
        )}
      </Modal>
    </div>
  );
};

export default ProductDetailsCta;
